/**
 * Attribute used to make a HTML element sticky.
 *
 * Usage:
 * ------
 *
 * ```html
  <div style="position: relative;">
    <div
        data-kb-sticky
        data-kb-sticky-end="#stickyStop">
        THIS IS STICKY UNTIL ITS BOTTOM REACH THE TOP OF THE STOP
    </div>
  </div>

   <div id="stickyStop"></div>
   ```
 *
 * @export
 * @class StickyManager
 */
export class StickyManager {
  /**
   * The selector used to find all sticky elements, should work with
   * `document.querySelectorAll()`
   *
   * @private
   * @type {string}
   * @memberof StickyManager
   */
  private selector: string = "[data-kb-sticky]";

  /**
   * The name of the data attribute containing the stop element ID, in
   * camelCase, without the `data-` part.
   *
   * @private
   * @memberof StickyManager
   */
  private stickyStopDataAttribute = "kbStickyStop";

  /**
   * Create a new StickyManager instance.
   * @memberof StickyManager
   */
  constructor() {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        () => {
          this.onContentLoaded();
        },
        false
      );
    } else {
      this.onContentLoaded();
    }
  }

  /**
   * Register the sticky manager on every targeted elements.
   *
   * @private
   * @memberof StickyManager
   */
  private onContentLoaded(): void {
    const elements = document.querySelectorAll<HTMLElement>(this.selector);

    Array.prototype.forEach.call(elements, (el: HTMLElement) => {
      const stickyStopSelector = el.dataset[this.stickyStopDataAttribute];

      if (!stickyStopSelector) {
        return;
      }

      const stickyStart = el.parentElement;
      const stickyStop: HTMLElement | null = document.querySelector<
        HTMLElement
      >(stickyStopSelector);

      if (!stickyStart || !stickyStop) {
        return;
      }

      // Update the position of the sticky on content loaded because the content can already be scrolled
      this.updatePosition(el, stickyStart, stickyStop);

      // Update the position of the sticky after delay because the previous content can be not completely displayed
      setTimeout(() => { this.updatePosition(el, stickyStart, stickyStop) }, 300);

      // Update the position of the sticky on document scrolling event
      document.addEventListener("scroll", _ => {
        this.updatePosition(el, stickyStart, stickyStop);
      }, { passive: true });
    });
  }

  /**
   * @private
   * @param {HTMLElement} el the HTML sticky element
   * @param {HTMLElement} stickyStart the HTML start element
   * @param {HTMLElement} stickyStop  the HTML stop element
   */
  private updatePosition(el: HTMLElement, stickyStart: HTMLElement, stickyStop: HTMLElement): void {
    const startPosition = this.getOffsetFromTopOfThePage(stickyStart);
    const stopPosition = this.getOffsetFromTopOfThePage(stickyStop);

    // Top / Bottom space
    const space = 20;

    const newOffset = window.scrollY - startPosition + space;

    // Case when the sticky should be at the starting position
    if (newOffset < 0) {
      el.style.position = 'absolute';
      el.style.top = '0px';
      return;
    }

    // Case when the sticky should be at the stopping position
    const maximumOffset = Math.max(stopPosition - startPosition - el.clientHeight - space, 0);
    if (newOffset > maximumOffset) {
      el.style.position = 'absolute';
      el.style.top = `${maximumOffset}px`;
      return;
    }

    // Case when the sticky is between the starting and stopping position
    el.style.top = `${space}px`;
    el.style.position = 'fixed';
  }

  /**
   * Compute the offset of a HTML element from the top of the page.
   *
   * @private
   * @param {HTMLElement} el the HTML element
   * @returns {number} the offset
   * @memberof StickyManager
   */
  private getOffsetFromTopOfThePage(el: HTMLElement): number {
    return el.getBoundingClientRect().top + window.scrollY;
  }
}
